import { Injectable } from '@angular/core';
import { take } from 'rxjs';
import { SessionService } from '@shared/services/session.service';
import moment from 'moment';
import { Store } from '@ngrx/store';
import { resetAuthorizationKey } from '@features/orders/actions/reset-authorization-key.actions';
import { EsetSubscription } from '@shared/models/eset-subscription';

@Injectable({
  providedIn: 'root',
})
export class EsetSubscriptionsService {
  constructor(
    private sessionService: SessionService,
    private store: Store,
  ) {}

  isExpired(esetSubscription: EsetSubscription): boolean {
    let remaining_days = moment(esetSubscription.attributes.expiration_date)
      .startOf('day')
      .diff(moment().startOf('day'), 'days');
    return remaining_days < 0;
  }

  isExpiring(esetSubscription: EsetSubscription): boolean {
    let remaining_days = moment(esetSubscription.attributes.expiration_date)
      .startOf('day')
      .diff(moment().startOf('day'), 'days');
    let expiringLimit = ['trial', 'business_trial'].includes(esetSubscription.attributes.purchase_type) ? 15 : 30;
    return remaining_days >= 0 && remaining_days <= expiringLimit;
  }

  isRenewable(esetSubscription: EsetSubscription): boolean {
    let isRenewable = false;
    this.sessionService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        if (!!config && !!config.limits) {
          let remaining_days = moment(esetSubscription.attributes.expiration_date)
            .startOf('day')
            .diff(moment().startOf('day'), 'days');
          if (
            (remaining_days < 0 || remaining_days <= config.limits.renew) &&
            !(
              esetSubscription.attributes.period == 'month_1' && esetSubscription.attributes.billing_interval == 'daily'
            ) &&
            !esetSubscription.attributes.auto_renew &&
            esetSubscription.attributes.subscription_status === 'active' &&
            !['trial', 'business_trial'].includes(esetSubscription.attributes.purchase_type)
          ) {
            isRenewable = true;
          }
        }
      });
    return isRenewable;
  }

  canBeExtended(esetSubscription: EsetSubscription): boolean {
    let canBeExtended = false;
    this.sessionService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        if (!!config && !!config.limits) {
          let remaining_days = moment(esetSubscription.attributes.expiration_date)
            .startOf('day')
            .diff(moment().startOf('day'), 'days');
          if (
            remaining_days > config.limits.renew &&
            !(
              esetSubscription.attributes.period == 'month_1' && esetSubscription.attributes.billing_interval == 'daily'
            ) &&
            !esetSubscription.attributes.auto_renew &&
            esetSubscription.attributes.subscription_status === 'active' &&
            !['trial', 'business_trial'].includes(esetSubscription.attributes.purchase_type)
          ) {
            canBeExtended = true;
          }
        }
      });
    return canBeExtended;
  }

  canBeConverted(esetSubscription: EsetSubscription): boolean {
    return (
      esetSubscription.attributes.subscription_status === 'active' &&
      ['trial', 'business_trial'].includes(esetSubscription.attributes.purchase_type)
    );
  }

  isUpgradeable(esetSubscription: EsetSubscription): boolean {
    let isUpgradeable = false;
    this.sessionService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        if (!!config && !!config.limits) {
          let remaining_days = moment(esetSubscription.attributes.expiration_date)
            .startOf('day')
            .diff(moment().startOf('day'), 'days');
          if (
            remaining_days > config.limits.enlarge &&
            esetSubscription.attributes.subscription_status === 'active' &&
            !(
              esetSubscription.attributes.period == 'month_1' && esetSubscription.attributes.billing_interval == 'daily'
            ) &&
            !!esetSubscription.attributes.expiration_date &&
            !['nfr', 'trial', 'business_trial'].includes(esetSubscription.attributes.purchase_type)
          ) {
            isUpgradeable = true;
          }
        }
      });
    return isUpgradeable;
  }

  canBeCancelled(esetSubscription: EsetSubscription): boolean {
    let canBeCancelled = false;
    this.sessionService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        if (
          !!config &&
          !!config.limits &&
          esetSubscription.attributes.subscription_status === 'active' &&
          !(
            esetSubscription.attributes.period == 'month_1' && esetSubscription.attributes.billing_interval == 'daily'
          ) &&
          !esetSubscription.attributes.auto_renew &&
          !!esetSubscription.attributes.expiration_date &&
          !['nfr', 'trial', 'business_trial'].includes(esetSubscription.attributes.purchase_type)
        ) {
          canBeCancelled = moment(esetSubscription.attributes.expiration_date).isSameOrBefore(
            moment().add(config.limits.cancel_license, 'days'),
          );
        }
      });
    return canBeCancelled;
  }

  resetAuthorizationKey(esetSubscriptionId: number, disconnectSeats: boolean) {
    this.store.dispatch(resetAuthorizationKey({ esetSubscriptionId, disconnectSeats }));
  }
}
